import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, FilePicker, VeneerFileInputFileList} from '@veneer/core';

import {ResponseError} from '@br/idm-api';
import {useCustomerApi} from '@br/idm-components';

import {ApiError, assertIsNotNullish} from '../../../util';
import {LicenseCheckErrorsStepProps} from '../LicenseImportSteps';

import LicenseValidateError from './LicenseValidateError';
import LicenseValidateSuccess from './LicenseValidateSuccess';
import useStyles from './styles';
import {asyncReadFile} from './utils';

const LicenseCheckErrorsForm = ({onProceed, onCsvFileContentChanged}: LicenseCheckErrorsStepProps): JSX.Element => {
    const {t} = useTranslation(['licenses']);
    const styles = useStyles();
    const {supportApi} = useCustomerApi();
    const [errorCsv, setErrorCsv] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<Array<string>>([]);

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [hasValidated, setHasValidated] = useState(false);

    const handleFileChanged = useCallback(
        (event: React.SyntheticEvent<HTMLInputElement>, files: VeneerFileInputFileList) => {
            setHasValidated(false);
            setValidationErrors([]);

            setUploadedFile(files[0]);
        },
        []
    );

    const onLicenseFileUploadCleared = useCallback(() => {
        // Reset the form state
        setValidationErrors([]);
        setUploadedFile(null);
        setIsValidating(false);
        setHasValidated(false);

        onCsvFileContentChanged('');
    }, [onCsvFileContentChanged]);

    const validateClicked = useCallback(() => {
        setIsValidating(true);

        // Being able to click "Validate" is predicated on there being a file.
        assertIsNotNullish(uploadedFile);

        asyncReadFile(uploadedFile)
            .then(
                csvFileContent => {
                    supportApi
                        .supportLicenseCheckErrorsCreate({
                            licenseUploadCheckErrorsRequest: {
                                csvFileContent,
                            },
                        })
                        .then(
                            response => {
                                setErrorCsv(response.hasErrors ? response.errorCsv : '');
                                setValidationErrors([]);
                                onCsvFileContentChanged(csvFileContent);
                            },
                            async (error: ResponseError) => {
                                let contentErrors: Array<string> = [];
                                if (error.response.status === 400) {
                                    const response = (await error.response.json()) as ApiError;
                                    contentErrors = response.csv_file_content as Array<string>;
                                } else {
                                    // Use a generic error when we don't know what happened
                                    contentErrors = [t('licenses:csvFileReadError', 'Error uploading CSV file')];
                                }
                                setValidationErrors(contentErrors);
                            }
                        )
                        .finally(() => {
                            setHasValidated(true);
                        });
                },
                () => {
                    setHasValidated(true);
                    setValidationErrors([t('licenses:csvFileReadError', 'Error uploading CSV file')]);
                }
            )
            .finally(() => {
                setIsValidating(false);
            });
    }, [onCsvFileContentChanged, supportApi, t, uploadedFile]);

    const showFilePicker = useMemo(() => {
        // don't show if it has not validated yet, or has validated and has errors
        return !hasValidated || (hasValidated && (validationErrors.length > 0 || errorCsv !== ''));
    }, [errorCsv, hasValidated, validationErrors]);

    return (
        <>
            {showFilePicker && (
                <div className={styles.fileImport}>
                    <FilePicker
                        id="import-licenses"
                        /* eslint-disable-next-line i18next/no-literal-string */
                        accept=".csv"
                        label={t('licenses:selectLicensesFile', 'Select licenses CSV file')}
                        helperText={validationErrors.join('\n')}
                        error={validationErrors.length > 0}
                        onChange={handleFileChanged}
                        onDrop={handleFileChanged}
                        onClear={onLicenseFileUploadCleared}
                        disabled={isValidating}
                        className={styles.filePicker}
                    />
                    <div>
                        <Button
                            type="button"
                            appearance="secondary"
                            disabled={!uploadedFile || isValidating}
                            onClick={validateClicked}
                        >
                            {t('licenses:validate', 'Validate')}
                        </Button>
                    </div>
                </div>
            )}
            {isValidating || !hasValidated || validationErrors.length > 0 ? null : errorCsv === '' ? (
                <LicenseValidateSuccess onProceed={onProceed} />
            ) : (
                <LicenseValidateError csvFileContent={errorCsv} />
            )}
        </>
    );
};

export default LicenseCheckErrorsForm;
